import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, GripVertical, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useDashboard } from "@/context/DashboardContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { SEVERITY_META } from "@/lib/meta";
import { RUNBOOK } from "@/constants/testIds";

const CATEGORIES = ["Database", "Networking", "Kubernetes", "Incident Management", "Security", "CI/CD"];
const ENVIRONMENTS = ["Production", "Staging", "Edge"];

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const emptyStep = () => ({ _key: uid(), title: "", description: "", command: "", warning: "" });

const blankForm = {
  title: "",
  category: "",
  severity: "",
  environment: "",
  summary: "",
  estimated_time: "",
  tags: "",
  target_services: "",
  steps: [emptyStep()],
};

export default function RunbookEditor({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { reload, meta } = useDashboard();
  const categories = meta?.categories || CATEGORIES;
  const isEdit = mode === "edit";

  const [form, setForm] = useState(blankForm);
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState({});
  const [cancelOpen, setCancelOpen] = useState(false);

  const loadExisting = useCallback(async () => {
    try {
      const { data } = await api.get(`/runbooks/${id}`);
      setForm({
        title: data.title,
        category: data.category,
        severity: data.severity,
        environment: data.environment,
        summary: data.summary || "",
        estimated_time: data.estimated_time || "",
        tags: (data.tags || []).join(", "),
        target_services: (data.target_services || []).join(", "),
        steps: data.steps.length ? data.steps.map((s) => ({ ...s, _key: uid() })) : [emptyStep()],
      });
      setVersion(data.version);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEdit) loadExisting();
  }, [isEdit, loadExisting]);

  // warn on browser close/refresh with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  };

  const updateStep = (i, patch) => {
    setForm((f) => ({ ...f, steps: f.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) }));
    setDirty(true);
  };

  const addStep = () => update({ steps: [...form.steps, emptyStep()] });
  const removeStep = (i) => {
    if (form.steps.length === 1) return;
    update({ steps: form.steps.filter((_, idx) => idx !== i) });
  };

  const validate = () => {
    const e = {};
    if (form.title.trim().length < 5) e.title = "Title must be at least 5 characters.";
    if (form.category.trim().length < 2) e.category = "Enter a category (at least 2 characters).";
    if (!form.severity) e.severity = "Select a severity.";
    if (!form.environment) e.environment = "Select an environment.";
    const badStep = form.steps.findIndex((s) => !s.title.trim());
    if (badStep >= 0) e.steps = `Step ${badStep + 1} needs a title.`;
    const emptyStepIdx = form.steps.findIndex((s) => !s.description.trim() && !s.command.trim());
    if (e.steps === undefined && emptyStepIdx >= 0) e.steps = `Step ${emptyStepIdx + 1} needs a description or a command.`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    category: form.category.trim(),
    severity: form.severity,
    environment: form.environment,
    summary: form.summary.trim(),
    estimated_time: form.estimated_time.trim(),
    tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    target_services: form.target_services.split(",").map((t) => t.trim()).filter(Boolean),
    steps: form.steps.map((s) => ({
      title: s.title.trim(),
      description: s.description.trim(),
      command: s.command.trim(),
      warning: s.warning.trim(),
    })),
  });

  const save = async () => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/runbooks/${id}`, buildPayload(), { params: { expected_version: version } });
        setVersion(data.version);
        setDirty(false);
        toast.success("Runbook saved");
        reload();
        navigate(`/runbooks/${id}`);
      } else {
        const { data } = await api.post(`/runbooks`, buildPayload());
        setDirty(false);
        toast.success("Runbook created");
        reload();
        navigate(`/runbooks/${data.id}`);
      }
    } catch (e) {
      // input is preserved; user can retry without losing work
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const attemptCancel = () => {
    if (dirty) setCancelOpen(true);
    else navigate(isEdit ? `/runbooks/${id}` : "/");
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 tr-fade-up">
      <Button variant="ghost" onClick={attemptCancel} className="gap-2" data-testid={RUNBOOK.cancelBtn}>
        <ArrowLeft className="h-4 w-4" /> {isEdit ? "Back to runbook" : "Back to library"}
      </Button>

      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
          {isEdit ? "Edit runbook" : "Create runbook"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Document a clear, repeatable operational procedure.</p>
      </div>

      <form
        data-testid={RUNBOOK.editorForm}
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
        className="space-y-6"
      >
        <Field label="Title" error={errors.title} required>
          <Input
            data-testid={RUNBOOK.fieldTitle}
            value={form.title}
            onChange={(e) => update({ title: e.target.value })}
            placeholder="e.g. Recover a Crashed PostgreSQL Primary"
          />
        </Field>

        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Category" error={errors.category} required hint="Pick an existing category or type a new one">
            <Input
              data-testid={RUNBOOK.fieldCategory}
              list="category-options"
              value={form.category}
              onChange={(e) => update({ category: e.target.value })}
              placeholder="e.g. Database or a new category"
              maxLength={40}
            />
            <datalist id="category-options">
              {categories.map((c) => <option key={c} value={c} />)}
            </datalist>
          </Field>
          <Field label="Severity" error={errors.severity} required>
            <Select value={form.severity} onValueChange={(v) => update({ severity: v })}>
              <SelectTrigger data-testid={RUNBOOK.fieldSeverity}><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-popover">
                {Object.entries(SEVERITY_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Environment" error={errors.environment} required>
            <Select value={form.environment} onValueChange={(v) => update({ environment: v })}>
              <SelectTrigger data-testid={RUNBOOK.fieldEnvironment}><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-popover">
                {ENVIRONMENTS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Summary">
          <Textarea
            data-testid={RUNBOOK.fieldSummary}
            value={form.summary}
            onChange={(e) => update({ summary: e.target.value })}
            placeholder="A short description of when and why to use this runbook."
          />
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Estimated time">
            <Input
              data-testid={RUNBOOK.fieldEstimated}
              value={form.estimated_time}
              onChange={(e) => update({ estimated_time: e.target.value })}
              placeholder="e.g. 20 min"
            />
          </Field>
          <Field label="Tags (comma separated)">
            <Input
              data-testid={RUNBOOK.fieldTags}
              value={form.tags}
              onChange={(e) => update({ tags: e.target.value })}
              placeholder="postgres, failover, incident"
            />
          </Field>
        </div>

        <Field label="Target services (comma separated)">
          <Input
            data-testid={RUNBOOK.fieldServices}
            value={form.target_services}
            onChange={(e) => update({ target_services: e.target.value })}
            placeholder="orders-api, billing-service"
          />
        </Field>

        {/* Steps */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base">Steps <span className="text-red-500">*</span></Label>
            {errors.steps && <span className="text-sm text-red-500">{errors.steps}</span>}
          </div>

          {form.steps.map((step, i) => (
            <div key={step._key} className="rounded-xl border border-border bg-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <GripVertical className="h-4 w-4" /> Step {i + 1}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeStep(i)}
                  disabled={form.steps.length === 1}
                  data-testid={RUNBOOK.removeStepBtn(i)}
                  className="text-red-500 hover:text-red-500 disabled:opacity-30"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <Input
                data-testid={RUNBOOK.stepTitle(i)}
                value={step.title}
                onChange={(e) => updateStep(i, { title: e.target.value })}
                placeholder="Step title"
              />
              <Textarea
                data-testid={RUNBOOK.stepDesc(i)}
                value={step.description}
                onChange={(e) => updateStep(i, { description: e.target.value })}
                placeholder="What to do and why"
                className="min-h-[60px]"
              />
              <Textarea
                data-testid={RUNBOOK.stepCommand(i)}
                value={step.command}
                onChange={(e) => updateStep(i, { command: e.target.value })}
                placeholder="Command (optional)"
                className="min-h-[100px] font-mono text-sm"
                spellCheck={false}
              />
              <Input
                data-testid={RUNBOOK.stepWarning(i)}
                value={step.warning}
                onChange={(e) => updateStep(i, { warning: e.target.value })}
                placeholder="Warning / prerequisite (optional)"
              />
            </div>
          ))}

          <Button type="button" variant="outline" onClick={addStep} className="gap-2" data-testid={RUNBOOK.addStepBtn}>
            <Plus className="h-4 w-4" /> Add step
          </Button>
        </div>

        <div className="flex items-center gap-3 border-t border-border pt-5">
          <Button type="submit" disabled={saving} className="gap-2" data-testid={RUNBOOK.saveBtn}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isEdit ? "Save changes" : "Create runbook"}
          </Button>
          <Button type="button" variant="ghost" onClick={attemptCancel}>Cancel</Button>
        </div>
      </form>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent className="bg-popover">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. If you leave now, they'll be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Keep editing</Button>
            <Button variant="destructive" onClick={() => navigate(isEdit ? `/runbooks/${id}` : "/")}>Discard</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, error, required, hint, children }) {
  return (
    <div className="space-y-2">
      <Label>{label} {required && <span className="text-red-500">*</span>}</Label>
      {children}
      {error ? (
        <p className="text-sm text-red-500" data-testid={`editor-error-${String(label).toLowerCase().replace(/\s+/g, "-")}`}>{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
